import { CommonModule } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatPaginatorModule } from "@angular/material/paginator";
import { BrowserModule } from "@angular/platform-browser";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartingModule } from "@tylertech/charting-module";
import { CommandsModule } from "@tylertech/commands-module";
import { CommonComponentsModule } from "@tylertech/common-components-module";
import { CommonDialogsModule } from "@tylertech/common-dialogs-module";
import { CoreModule } from "@tylertech/core-module";
import { CustomViewModule } from "@tylertech/custom-view";
import { IncodeModule } from "@tylertech/incode-module";
import { IntentsModule } from "@tylertech/intents-module";
import { LgdDatePickerModule } from "@tylertech/lgd-reactive-forms";
import { NotesModule } from "@tylertech/notes-module";
import { PersonnelModule } from "@tylertech/personnel-module";
import
{
    AttachmentActionHandler,
    DialogActionHandler,
    FinanceApprovalActionHandler,
    IntentActionHandler,
    MetadataActionHandler,
    NoteActionHandler,
    PopupActionHandler,
    ShareActionHandler,
    UrlActionHandler
} from "./actionHandlers";
import
{
    AggregateCardComponent,
    AggregateChartComponent,
    AggregateCollectionComponent,
    CardElementComponent,
    DetailDialogComponent,
    DetailsComponent,
    StandardTaskComponent,
    TasksComponent
} from "./components";
import { ActionHandlerType, AGGREGATE_ACTION_HANDLERS } from "./interfaces";
import
{
    AggregateActionHandlerRepository,
    AggregateActionService,
    AggregateHelperService,
    AggregateManagerService,
    AggregateService,
    DetailDialogService,
} from "./services";

const angularModules = [
    BrowserModule,
    CommonModule,
    FlexModule,
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
];

const angularMaterialModules = [
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatDividerModule,
    MatIconModule,
    MatPaginatorModule,
];

const lgdModules = [
    CommandsModule,
    CommonComponentsModule,
    CoreModule,
    CommonDialogsModule,
    IntentsModule,
    IncodeModule,
    CustomViewModule,
    ChartJsContainerComponent,
    ChartingModule,
    LgdDatePickerModule,
    NotesModule,
    // TODO - this dependency must be flipped. library/aggregates should be lower in the dep tree than a Product Module.
    PersonnelModule,
];

const exportedComponents = [
    TasksComponent,
    AggregateChartComponent,
    AggregateCardComponent
];

const standardAggregateActionHandlers: ReadonlyArray<ActionHandlerType> = [
    DialogActionHandler,
    IntentActionHandler,
    PopupActionHandler,
    ShareActionHandler,
    UrlActionHandler,
    MetadataActionHandler,
    FinanceApprovalActionHandler,
    NoteActionHandler,
    AttachmentActionHandler,
];

@NgModule({
    imports: [
        ...angularModules,
        ...angularMaterialModules,
        ...lgdModules
    ],
    declarations: [
        ...exportedComponents,
        CardElementComponent,
        AggregateCollectionComponent,
        AggregateCardComponent,
        StandardTaskComponent,
        DetailDialogComponent,
        DetailsComponent
    ],
    exports: exportedComponents,
    providers: [
        {
            provide: AGGREGATE_ACTION_HANDLERS,
            multi: true,
            useValue: standardAggregateActionHandlers,
        },
        ...standardAggregateActionHandlers,
        AggregateManagerService,
        AggregateHelperService,
        AggregateActionService,
        AggregateActionHandlerRepository,
        DetailDialogService,
        AggregateService,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AggregatesModule
{
    static withActionHandlers(...handlers: ReadonlyArray<ActionHandlerType>): ModuleWithProviders<AggregatesModule>
    {
        /*
         * Checking the registry here instead of in the service, so that hopefully the errors will specify where the module is being configured incorrectly.
         */
        if (!handlers)
        {
            throw new Error(`handlers cannot be falsy.`);
        }

        return {
            ngModule: AggregatesModule,
            providers: [{
                provide: AGGREGATE_ACTION_HANDLERS,
                multi: true,
                useValue: handlers,
            },],
        };
    }
}
